import { Link2, MousePointerClick, Repeat2 } from "lucide-react";
import { DiscoveryMetric, DiscoveryPerformance } from "./types";
import { DateRange, PeriodDropdown, PeriodValue } from "../../../components/period-filter";

interface StatConfig {
  key: keyof DiscoveryPerformance;
  label: string;
  description: string;
  icon: React.ElementType;
}

const STAT_CONFIG: StatConfig[] = [
  {
    key: "links_generated",
    label: "Links Generated",
    description: "Total number of Discovery Links generated",
    icon: Link2,
  },
  {
    key: "new_clicks_recorded",
    label: "New Clicks Recorded",
    description: "Valid unique clicks recorded across Discovery VouchLinks",
    icon: MousePointerClick,
  },
  {
    key: "qualified_links",
    label: "Qualified Links",
    description: "Qualified links that turned into earnings.",
    icon: Repeat2,
  },
];

const DiscoveryStatCard: React.FC<{ config: StatConfig; metric?: DiscoveryMetric; loading: boolean }> = ({
  config,
  metric,
  loading,
}) => {
  const Icon = config.icon;
  const changeAbsolute = metric?.change_absolute ?? 0;
  const isPositive = changeAbsolute >= 0;
  const hasChange = !loading && changeAbsolute !== 0;
  const displayChange =
    metric?.change_percent !== null && metric?.change_percent !== undefined
      ? `${isPositive ? "+" : "-"}${Math.abs(metric.change_percent)}%`
      : `${isPositive ? "+" : "-"}${Math.abs(changeAbsolute)}`;

  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm">
      <div className="text-gray-600 mb-4">
        <Icon size={22} />
      </div>
      <h3 className="font-semibold text-[#121212] text-[18px] mb-1">{config.label}</h3>
      <p className="text-[14px] text-[#8494AE] mb-4">{config.description}</p>
      <div className="flex items-end gap-3">
        <span className="text-[22px] font-bold text-[#333D4D]">{loading ? "–" : metric?.value ?? 0}</span>
        {hasChange && (
          <span
            className={`text-sm font-medium px-2 py-1 rounded-full text-white ${isPositive ? "bg-[#00A86B]" : "bg-[#F37A50]"
              }`}
          >
            {displayChange}
          </span>
        )}
      </div>
      <p className="text-[#121212] text-[12px] mt-2">vs Last period</p>
    </div>
  );
};

interface DiscoveryStatsProps {
  performance: DiscoveryPerformance | null;
  loading: boolean;
  period: PeriodValue;
  onPeriodChange: (period: PeriodValue) => void;
  customRange: DateRange;
  onCustomRangeChange: (range: DateRange) => void;
}

export const DiscoveryStats: React.FC<DiscoveryStatsProps> = ({
  performance, loading, period, onPeriodChange, customRange, onCustomRangeChange,
}) => (
  <div className="bg-gradient-to-r from-[#E5F6F0] to-[#7E8B7E00] p-4 sm:p-7 rounded-[24px]">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-[18px] text-[#003320] font-semibold">Discovery Performance</h2>
      <PeriodDropdown value={period} onChange={onPeriodChange} customRange={customRange} onCustomRangeChange={onCustomRangeChange} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {STAT_CONFIG.map((config) => (
        <DiscoveryStatCard key={config.key} config={config} metric={performance?.[config.key]} loading={loading} />
      ))}
    </div>
  </div>
);
